import React from 'react';
import propTypes from 'prop-types';
import { FormGroup, Label, Input, Row, Col } from 'reactstrap';
import ChecklistBox, { singleOf, idsOf } from '../../../components/Export/ChecklistBox';
import { loadRacks, loadSellers, loadSizes, loadRarities, loadSubcategories } from '../../../utils/exportOptions';
import { warehouseStockStatusOptions } from '../warehouse/FilterPanel';

const sectionStyle = { fontSize: '.66rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase' };
const labelStyle = { fontWeight: 600, fontSize: '.78rem' };

export const STATUS_OPTIONS = [...warehouseStockStatusOptions.map(o => ({ id: o.value, name: o.label })), { id: 'approved', name: 'Approved' }];
export const CONDITION_OPTIONS = [{ id: 'BARU', name: 'BARU' }, { id: 'STANDARD_99', name: 'Standard 99%' }, { id: 'BEKAS', name: 'BEKAS' }];
export const FULFILMENT_OPTIONS = [{ id: '0', name: 'Ready stock' }, { id: '1', name: 'Pre-order' }];
export const STOCK_OPTIONS = [{ id: 'in', name: 'Quantity > 0' }, { id: 'zero', name: 'Zero quantity' }];
export const CONSIGNMENT_OPTIONS = [{ id: '1', name: 'Consignment' }, { id: '0', name: 'Own stock' }];

export const WAREHOUSE_EXPORT_DEFAULTS = {
  status: [{ id: 'pre_verified_approved', name: 'Pre-verified Approved' }],
  sneakers_condition: [],
  fulfilment: [],
  stock: [{ id: 'in', name: 'Quantity > 0' }],
  consignment: [],
  rack: [],
  seller: [],
  size: [],
  keyword: '',
  rarities: [],
  languages: [],
  subcategories: [],
  date_field: 'approved_at',
  date_from: '',
  date_to: ''
};

const Field = ({ id, label, children }) => (
  <FormGroup>
    <Label for={id} className="mb-1" style={labelStyle}>{label}</Label>
    {children}
  </FormGroup>
);

// WarehouseExportFilters is the Warehouse block inside ExportModal. Every
// option control is a ChecklistBox; toWarehouseParams turns the picks into
// the query parameters Go's /admins/sells understands.
export default function WarehouseExportFilters({ filters, setFilter, disabled, loadLanguages }) {
  const box = (name, props) => (
    <ChecklistBox id={`_exportWh_${name}`} value={filters[name]} onChange={v => setFilter(name, v)} disabled={disabled} {...props} />
  );
  return (
    <div>
      <div className="text-muted mb-2" style={sectionStyle}>Listing</div>
      <Row>
        <Col md={6}><Field id="_exportWh_status" label="Status">{box('status', { options: STATUS_OPTIONS, placeholder: 'All statuses' })}</Field></Col>
        <Col md={6}><Field id="_exportWh_sneakers_condition" label="Condition">{box('sneakers_condition', { options: CONDITION_OPTIONS, placeholder: 'All conditions' })}</Field></Col>
      </Row>
      <Row>
        <Col md={4}><Field id="_exportWh_fulfilment" label="Fulfilment">{box('fulfilment', { options: FULFILMENT_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportWh_stock" label="Stock">{box('stock', { options: STOCK_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportWh_consignment" label="Consignment">{box('consignment', { options: CONSIGNMENT_OPTIONS })}</Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Where and who</div>
      <Row>
        <Col md={6}><Field id="_exportWh_rack" label="Rack">{box('rack', { loadOptions: loadRacks, searchable: true, placeholder: 'All racks' })}</Field></Col>
        <Col md={6}><Field id="_exportWh_seller" label="Seller">{box('seller', { loadOptions: loadSellers, searchable: true, placeholder: 'All sellers' })}</Field></Col>
      </Row>
      <Row>
        <Col md={6}><Field id="_exportWh_size" label="Size / grade">{box('size', { loadOptions: loadSizes, searchable: true, placeholder: 'All sizes' })}</Field></Col>
        <Col md={6}><Field id="_exportWh_keyword" label="Keyword">
          <Input id="_exportWh_keyword" bsSize="sm" value={filters.keyword} disabled={disabled} placeholder="Display name, SKU, CG-id, seller email" onChange={e => setFilter('keyword', e.target.value)} />
        </Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Card</div>
      <Row>
        <Col md={4}><Field id="_exportWh_rarities" label="Rarity">{box('rarities', { loadOptions: loadRarities, searchable: true, placeholder: 'All rarities' })}</Field></Col>
        <Col md={4}><Field id="_exportWh_languages" label="Language">{box('languages', { loadOptions: loadLanguages, placeholder: 'All languages' })}</Field></Col>
        <Col md={4}><Field id="_exportWh_subcategories" label="Subcategory">{box('subcategories', { loadOptions: loadSubcategories, searchable: true, placeholder: 'All subcategories' })}</Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Dates</div>
      <Row>
        <Col md={4}><Field id="_exportWh_date_field" label="Date field">
          <Input id="_exportWh_date_field" bsSize="sm" type="select" value={filters.date_field} disabled={disabled} onChange={e => setFilter('date_field', e.target.value)}>
            <option value="approved_at">Approved at</option>
            <option value="created_at">Created at</option>
            <option value="updated_at">Updated at</option>
          </Input>
        </Field></Col>
        <Col md={4}><Field id="_exportWh_date_from" label="From">
          <Input id="_exportWh_date_from" bsSize="sm" type="date" value={filters.date_from} disabled={disabled} onChange={e => setFilter('date_from', e.target.value)} />
        </Field></Col>
        <Col md={4}><Field id="_exportWh_date_to" label="To">
          <Input id="_exportWh_date_to" bsSize="sm" type="date" value={filters.date_to} disabled={disabled} onChange={e => setFilter('date_to', e.target.value)} />
        </Field></Col>
      </Row>
    </div>
  );
}

WarehouseExportFilters.propTypes = {
  filters: propTypes.object.isRequired,
  setFilter: propTypes.func.isRequired,
  disabled: propTypes.bool,
  loadLanguages: propTypes.func.isRequired
};


// toWarehouseParams maps the modal's values onto Go /admins/sells query
// parameters. The panel's own params come first; an empty box inherits the
// panel's value, a pick overrides it. Two-option boxes narrow only when
// exactly one option is ticked; Stock ticks both or none to drop has_quantity.
export const toWarehouseParams = (panelParams, f) => {
  const { page, per_page, sort_by, seller, ...base } = panelParams;
  const params = { ...base };
  const override = (key, value) => {
    if (value !== '' && value !== null && value !== undefined) params[key] = value;
  };
  override('status', idsOf(f.status));
  override('sneakers_condition', idsOf(f.sneakers_condition));
  override('pre_order', singleOf(f.fulfilment, FULFILMENT_OPTIONS));
  const stock = singleOf(f.stock, STOCK_OPTIONS);
  if (stock === 'in') { params.has_quantity = 'true'; delete params.quantity; }
  if (stock === 'zero') { delete params.has_quantity; params.quantity = '0'; }
  if (stock === '' && (f.stock || []).length > 0) delete params.has_quantity;
  override('consignment', singleOf(f.consignment, CONSIGNMENT_OPTIONS));
  override('rack', (f.rack || []).map(r => r.name).join(', '));
  override('seller_id', (f.seller || []).length === 1 ? String(f.seller[0].id) : idsOf(f.seller));
  override('size', idsOf(f.size));
  override('keyword', (f.keyword || '').trim());
  override('rarity_id', idsOf(f.rarities));
  override('language', idsOf(f.languages));
  override('subcategory_id', idsOf(f.subcategories));
  if (f.date_from || f.date_to) {
    override('date_field', f.date_field);
    override('date_from', f.date_from);
    override('date_to', f.date_to);
  }
  return params;
};
